// @flow
import * as React from 'react';

import get from 'lodash/get';

import {
  ChoiceField,
  ChoiceFieldGroup,
  useBasicValidators,
} from '@gandi/form/FinalForm';
import { useTranslator } from '@gandi/translate';
import { Base, Block, BlockHeading, Placeholder, SrOnly } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';
import { useFieldWithTypeValue } from 'src/hooks/useFieldWithTypeValue';
import { selectSellableMailboxOffers } from 'src/modules/mailbox/offers';

import Price from '../../shared/Price';
import {
  MAILBOX_DURATION_INPUT_NAME,
  MAILBOX_TYPE_INPUT_NAME,
  initialFormValuesDefault,
} from '../consts';
import {
  useMailboxLabel,
  useDurationTranslations,
  useMailboxCatalog,
} from '../hooks';
import type { FormValues } from '../types/form';

const PricePlaceholder = () => (
  <Base flex flexCol flexAlignItemsStart gap="xsmall">
    <Placeholder display="inline-block" chars={5} />
    <Placeholder display="inline-block" chars={10} />
  </Base>
);

export function MailboxTypeChoiceGroup(): React.MixedElement {
  const { __ } = useTranslator();
  const { required } = useBasicValidators();

  const duration = useFieldWithTypeValue<
    FormValues[typeof MAILBOX_DURATION_INPUT_NAME],
  >(MAILBOX_DURATION_INPUT_NAME);

  const mailboxType = useFieldWithTypeValue<
    FormValues[typeof MAILBOX_TYPE_INPUT_NAME],
  >(MAILBOX_TYPE_INPUT_NAME);

  const minDuration = parseInt(duration, 10) || 1;

  const getMailboxLabel = useMailboxLabel();
  const { unitToLabel } = useDurationTranslations();
  const { prices, currency, sharingId } = useMailboxCatalog();
  const boxes = useAppSelector(state =>
    selectSellableMailboxOffers(state, sharingId),
  );

  return (
    <Block>
      <BlockHeading>{__('What type of mailbox do you want?')}</BlockHeading>
      <Base>
        <ChoiceFieldGroup
          name={MAILBOX_TYPE_INPUT_NAME}
          validate={required}
          choiceGroupProps={{
            legend: __('Type of mailbox'),
            layout: 'row',
            value: mailboxType,
          }}
        >
          {boxes.length ? (
            boxes.map(({ mailbox_type, name, capacity }) => {
              const price = get(
                prices,
                `${mailbox_type}.${minDuration}.priceAfterTaxes`,
                0,
              );
              const normalPrice = get(
                prices,
                `${mailbox_type}.${minDuration}.normalPriceAfterTaxes`,
                undefined,
              );

              const durationUnit = get(
                prices,
                `${mailbox_type}.durationUnit`,
                'm',
              );
              return (
                <ChoiceField
                  key={mailbox_type}
                  name={MAILBOX_TYPE_INPUT_NAME}
                  value={mailbox_type}
                >
                  <Base slot="label" weight="semibold" transform="capitalize">
                    {getMailboxLabel(name, capacity)}
                  </Base>
                  {price ? (
                    <Base slot="additional">
                      <Price
                        currency={currency}
                        price={price}
                        priceCrossedOut={normalPrice}
                        priceInfo={` ${unitToLabel[durationUnit]}`}
                      />
                    </Base>
                  ) : (
                    <PricePlaceholder slot="additional" />
                  )}
                </ChoiceField>
              );
            })
          ) : (
            <ChoiceField
              disabled
              name={MAILBOX_TYPE_INPUT_NAME}
              value={initialFormValuesDefault[MAILBOX_TYPE_INPUT_NAME]}
              style={{ maxWidth: '250px' }}
            >
              <Base slot="label">
                <SrOnly>{__('Loading offers...')}</SrOnly>
                <Placeholder display="inline-block" chars={15} />
              </Base>
              <PricePlaceholder slot="additional" />
            </ChoiceField>
          )}
        </ChoiceFieldGroup>
      </Base>
    </Block>
  );
}
